// alphaListGrouping.js

export const ALPHA_LIST_LETTERS = Object.freeze([
  '#',
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
]);

const DIACRITIC_MARKS = /[\u0300-\u036f]/g;

/**
 * First A–Z letter of a title, or `#` for empty / non-letter titles.
 *
 * @param {unknown} title
 * @returns {string}
 */
export function getAlphaListLetter(title) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return '#';

  const first = trimmed.charAt(0);
  const ascii = first.normalize('NFD').replace(DIACRITIC_MARKS, '').toLocaleUpperCase();
  if (ascii >= 'A' && ascii <= 'Z') return ascii;
  return '#';
}

/**
 * Locale-aware title compare with a stable key tie-break.
 *
 * @param {string} left
 * @param {string} right
 * @returns {number}
 */
export function compareAlphaListTitles(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Sorts nodes by title and groups them by first letter.
 *
 * @param {unknown[]} nodes
 * @returns {{
 *   groups: Array<{ letter: string, nodes: object[], startIndex: number, endIndex: number }>,
 *   letters: Set<string>,
 *   sortedNodes: object[],
 * }}
 */
export function groupNodesByAlphaLetter(nodes) {
  const source = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
  const sortedNodes = [...source].sort((left, right) => {
    const titleCompare = compareAlphaListTitles(left?.title, right?.title);
    if (titleCompare !== 0) return titleCompare;
    return String(left?.key || '').localeCompare(String(right?.key || ''));
  });

  const groups = [];
  const letters = new Set();
  let current = null;

  sortedNodes.forEach((node, index) => {
    const letter = getAlphaListLetter(node?.title);
    letters.add(letter);

    if (!current || current.letter !== letter) {
      current = {
        letter,
        nodes: [],
        startIndex: index,
        endIndex: index,
      };
      groups.push(current);
    }

    current.nodes.push(node);
    current.endIndex = index;
  });

  return {
    groups,
    letters,
    sortedNodes,
  };
}

/**
 * Nearest overflow-y scroll ancestor for sticky index / jump scrolling.
 *
 * @param {Element|null} el
 * @returns {Element|null}
 */
export function getAlphaListScrollParent(el) {
  let node = el?.parentElement || null;
  while (node) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
